// modules.json's pure half — 05j-1. module-loader.js's title → config lookup, the slug and
// the default size, copied exactly (incl. its `Number(x) || fallback` coercions). The live
// half (module-config.ts) owns the fetch and the side-panel tiles.

/** One modules.json entry — every key the file has today. */
export interface ModuleConfig {
  title: string;
  /** The side panel's short name. */
  label: string;
  slug: string;
  /** The component registry's key (component-registry.ts), e.g. "renderStatsModule". */
  renderer: string;
  cols: number;
  rows: number;
  needsPlayer: boolean;
  /** Only on the widgets a same-kind drop can merge (Stats, Radar). */
  mergeable?: boolean;
  /** Only on the Stats-category widgets (the player strip's filter, "All"'s goalkeeper). */
  categoryId?: string;
}

export interface GridSize {
  cols: number;
  rows: number;
}

/** A title as a file-name slug ("Bio" → "bio"). */
export function slugify(value: unknown): string {
  return (
    String(value || "module")
      .replace(/&/g, " and ")
      .replace(/[/—–]/g, " ")
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "module"
  );
}

/** The entry for a title (or a slug). Null before the list has loaded, or for an unknown one. */
export function findModuleConfig(list: readonly ModuleConfig[] | null, identifier: string | null | undefined): ModuleConfig | null {
  if (!identifier || !list) return null;
  return list.find((item) => item.title === identifier || item.slug === identifier) ?? null;
}

/** The entry's slug, else a slug made from the title. (module-loader.js also kept an empty
 *  static title → slug table in between — every loaded entry has a slug, so it never
 *  answered anything this lookup doesn't.) */
export function slugOf(cfg: ModuleConfig | null, title: string): string {
  return cfg?.slug || slugify(title);
}

/** The entry's size, else the fallback — never below 1 × 1. */
export function defaultSize(cfg: ModuleConfig | null, fallback: GridSize = { cols: 1, rows: 1 }): GridSize {
  const fallbackCols = Number(fallback.cols) || 1;
  const fallbackRows = Number(fallback.rows) || 1;
  return {
    cols: Math.max(1, Number(cfg?.cols) || fallbackCols),
    rows: Math.max(1, Number(cfg?.rows) || fallbackRows),
  };
}
